/**
 * Shared Types for Custom Config Field Renderers
 *
 * All custom renderers should use these types to ensure consistency
 * in field mapping support and interface patterns.
 */

import type { ConfigFieldDefinition, FieldType } from '@/types/workflowFields';
import { CONFIG_MAPPING_PREFIX } from '@/types/workflowFields';
import type { NodeData, NodeType } from '@/types/imageWorkflow';

// ============================================================================
// Field Source Mapping Types
// ============================================================================

/**
 * Represents a mapping from one field to another
 */
export interface FieldMapping {
  input_field_key: string;
  source_node_key: string;
  source_output_key: string;
  source_key_path?: string;
  is_auto_mapped: boolean;
}

/**
 * Handlers for managing field source mappings
 */
export interface FieldSourceHandlers {
  /** Current mapping for this field (if any) */
  mapping?: FieldMapping;
  /** Callback to set or update mapping */
  onMappingChange: (sourceNodeKey: string, sourceOutputKey: string, keyPath?: string) => void;
  /** Callback to update key path within mapping */
  onKeyPathChange: (keyPath: string | undefined) => void;
  /** Callback to remove mapping */
  onMappingRemove: () => void;
}

/**
 * Factory for creating field handlers for multiple subfields
 * Used by custom renderers that have multiple mappable fields
 */
export interface FieldSourceHandlersFactory {
  createFieldHandlers: (fieldKey: string) => FieldSourceHandlers;
}

// ============================================================================
// Available Source Types
// ============================================================================

/**
 * Output field from a source node
 */
export interface SourceOutputField {
  key: string;
  type: FieldType;
  label: string | undefined;
}

/**
 * A node that can be used as a source for field mapping
 */
export interface AvailableSource {
  node_key: string;
  node_type: string;
  node_label: string;
  outputs: SourceOutputField[];
}

/**
 * Context variable from workflow context
 */
export interface ContextVariable {
  key: string;
  label: string;
  type: FieldType;
}

/**
 * Variable defined in Start node
 */
export interface StartVariable {
  key: string;
  value: string;
}

/**
 * Custom input field defined on the node
 */
export interface CustomInputField {
  key: string;
  type: FieldType;
  label?: string;
}

// ============================================================================
// Base Renderer Props Interface
// ============================================================================

/**
 * Base props that all custom renderers receive.
 *
 * Custom renderers should extend this interface if they need additional props.
 * Renderers that support field mapping should use `fieldSourceHandlersFactory`
 * to create handlers for their mappable fields.
 *
 * @example
 * ```tsx
 * // For a renderer with mappable fields
 * const titleHandlers = fieldSourceHandlersFactory?.createFieldHandlers('__config__title');
 *
 * <FieldSourceToggle
 *   fieldKey="__config__title"
 *   mapping={titleHandlers?.mapping}
 *   onMappingChange={titleHandlers?.onMappingChange || (() => {})}
 *   onKeyPathChange={titleHandlers?.onKeyPathChange || (() => {})}
 *   onMappingRemove={titleHandlers?.onMappingRemove || (() => {})}
 *   availableSources={availableSources}
 *   customInputs={customInputs}
 *   contextVariables={contextVariables}
 *   startVariables={startVariables}
 * >
 *   <Input ... />
 * </FieldSourceToggle>
 * ```
 */
export interface BaseCustomRendererProps {
  /** Field definition from config_fields */
  field: ConfigFieldDefinition;
  /** Current value of the field */
  value: unknown;
  /** Callback when value changes */
  onChange: (value: unknown) => void;
  /** Full node data (for context and reading other fields) */
  nodeData: NodeData;
  /** Node type (for renderers that behave differently per node type) */
  nodeType?: NodeType;
  /** Whether editing is disabled (e.g., during execution) */
  isDisabled?: boolean;
  /** Callback to update multiple fields at once */
  onBatchUpdate?: (updates: Partial<NodeData>) => void;

  // ========== Field Mapping Support Props ==========

  /**
   * Direct handlers for THIS field (when field itself is mappable).
   * Usually provided by ConfigFieldRenderer when field.mapping_enabled is true.
   */
  fieldSourceHandlers?: FieldSourceHandlers;

  /**
   * Factory to create handlers for SUBFIELDS within the custom renderer.
   * Use this when your renderer has multiple input fields that should
   * support individual mapping (e.g., title + body in PushTargetRenderer).
   *
   * Field keys should use '__config__' prefix: '__config__fieldName'
   */
  fieldSourceHandlersFactory?: FieldSourceHandlersFactory;

  /** Available upstream nodes and their outputs for mapping */
  availableSources?: AvailableSource[];

  /** Custom input fields defined on this node */
  customInputs?: CustomInputField[];

  /** Context variables from workflow */
  contextVariables?: ContextVariable[];

  /** Variables defined in Start node */
  startVariables?: StartVariable[];
}

// ============================================================================
// Type Guards & Utilities
// ============================================================================

/**
 * Check if props include field mapping support
 */
export function hasFieldMappingSupport(
  props: BaseCustomRendererProps
): props is BaseCustomRendererProps & {
  fieldSourceHandlersFactory: FieldSourceHandlersFactory;
  availableSources: AvailableSource[];
} {
  return (
    props.fieldSourceHandlersFactory !== undefined &&
    props.availableSources !== undefined &&
    props.availableSources.length > 0
  );
}

/**
 * Re-export CONFIG_MAPPING_PREFIX as CONFIG_FIELD_PREFIX for backward compatibility.
 * Use CONFIG_MAPPING_PREFIX directly in new code.
 *
 * Config fields use '__config__fieldName' format in inputMappings.
 */
export { CONFIG_MAPPING_PREFIX as CONFIG_FIELD_PREFIX };

/**
 * Generate a config field key with the __config__ prefix.
 * Used by custom renderers to create unique keys for their internal fields.
 *
 * @example
 * ```ts
 * const titleKey = configFieldKey('title');  // '__config__title'
 * const handlers = factory.createFieldHandlers(titleKey);
 * ```
 */
export function configFieldKey(fieldName: string): string {
  return `${CONFIG_MAPPING_PREFIX}${fieldName}`;
}
